import { useSyncExternalStore } from 'react';

/**
 * Hell/Dunkel im Partnerportal (ADR 0085). Dieselben Tokens wie im ERP — der
 * `.dark`-Block in `packages/ui/src/styles/globals.css` schaltet um.
 *
 * Bewusst ohne Zustand-Bibliothek: Der Portal-Zustand besteht aus genau einem
 * Wert. Ein Store dafür wäre eine Abhängigkeit, die nichts trägt (CLAUDE.md
 * §14: keine neuen Pakete ohne Not).
 *
 * Voreinstellung ist die **Systemeinstellung** des Partners, nicht „hell".
 * Wer sein Gerät dunkel gestellt hat, will keine weiße Wand ins Gesicht.
 */
export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'proerp.portal.theme';

function systemTheme(): Theme {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

function readStored(): Theme | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    // Privater Modus / blockierter Speicher: dann eben ohne Erinnerung.
    return null;
  }
}

let current: Theme = readStored() ?? systemTheme();
const listeners = new Set<() => void>();

function apply(theme: Theme): void {
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

export function getTheme(): Theme {
  return current;
}

export function setTheme(theme: Theme): void {
  current = theme;
  apply(theme);
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // s. o. — die Anzeige stimmt trotzdem, nur die Erinnerung fehlt.
  }
  for (const listener of listeners) listener();
}

export function toggleTheme(): void {
  setTheme(current === 'dark' ? 'light' : 'dark');
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Reaktiver Zugriff für Komponenten. */
export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, getTheme, getTheme);
}

/** Vor dem ersten Render aufrufen (main.tsx) — sonst flackert es. */
export function initTheme(): void {
  apply(current);
}

/**
 * Respektiert `prefers-reduced-motion`. recharts animiert per JavaScript, nicht
 * per CSS — die Regel in `index.css` greift dort NICHT. Wer Bewegung
 * abgeschaltet hat, bekommt sonst trotzdem wachsende Balken und sich malende
 * Linien.
 */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false)
  );
}
